"use client";

import { useState } from "react";
import { resolveApiAssetUrl } from "@/lib/backend";
import { resolveVideoEmbed } from "@/lib/video-embed";
import { cn } from "@/lib/utils";

export type VideoSectionData = {
  provider?: "youtube" | "vimeo" | "file" | string;
  video_url?: string;
  poster_url?: string;
  heading?: string;
  caption?: string;
  aspect?: "16:9" | "4:3" | "1:1" | string;
};

const aspectClass: Record<string, string> = {
  "16:9": "aspect-video",
  "4:3": "aspect-[4/3]",
  "1:1": "aspect-square",
};

export function VideoSection({ data }: { data: VideoSectionData }) {
  const [activated, setActivated] = useState(false);
  const rawUrl = (data.video_url ?? "").trim();
  if (!rawUrl) return null;

  const aspect = aspectClass[data.aspect ?? "16:9"] ?? "aspect-video";
  const { provider, embedSrc, fileSrc: filePath } = resolveVideoEmbed(rawUrl, data.provider);
  const poster = resolveApiAssetUrl(data.poster_url);
  const fileSrc = provider === "file" ? resolveApiAssetUrl(filePath || rawUrl) : "";

  const title = data.heading?.trim() || data.caption?.trim() || "Embedded video";

  return (
    <section className="cms-section-contained py-[var(--section-space)]">
      <div className="mx-auto w-full max-w-5xl min-w-0 px-4 md:px-6">
        {data.heading ? (
          <h2 className="mb-6 text-balance text-center text-[clamp(1.5rem,2.6vw,2.25rem)] font-extrabold tracking-tight text-[var(--primary)]">
            {data.heading}
          </h2>
        ) : null}

        <div
          className={cn(
            "relative w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-900 shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5",
            aspect
          )}
        >
          {provider === "file" && fileSrc ? (
            <video
              src={fileSrc}
              poster={poster || undefined}
              controls
              preload="metadata"
              className="absolute inset-0 h-full w-full bg-black"
              aria-label={title}
            />
          ) : embedSrc ? (
            activated ? (
              <iframe
                src={`${embedSrc}${embedSrc.includes("?") ? "&" : "?"}autoplay=1`}
                title={title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full border-0"
              />
            ) : (
              <button
                type="button"
                onClick={() => setActivated(true)}
                className="group absolute inset-0 flex h-full w-full items-center justify-center bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
                aria-label={`Play video: ${title}`}
              >
                {poster ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={poster}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 h-full w-full max-w-full object-contain object-center opacity-90 motion-safe:transition-opacity motion-safe:group-hover:opacity-100"
                  />
                ) : null}
                <span
                  aria-hidden
                  className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-[var(--primary)] shadow-xl motion-safe:transition-transform motion-safe:group-hover:scale-110 md:h-20 md:w-20"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="ml-1 h-7 w-7 md:h-9 md:w-9">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </button>
            )
          ) : (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-slate-300">
              Unable to load video. Check the URL.
            </div>
          )}
        </div>

        {data.caption ? (
          <p className="mx-auto mt-4 max-w-3xl text-center text-sm text-slate-500 md:text-base">
            {data.caption}
          </p>
        ) : null}
      </div>
    </section>
  );
}
